import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Calendar, User, ArrowLeft, Clock, Share2, Twitter, Linkedin, Facebook } from 'lucide-react';
import { API_URL } from '../config';
import '../styles/Blog.css';


const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/blog/${slug}`);
      setPost(response.data);
      
      // Fetch related posts
      const allPosts = await axios.get(`${API_URL}/api/blog`);
      const related = allPosts.data
        .filter(p => p.slug !== slug && p.category === response.data.category)
        .slice(0, 3);
      setRelatedPosts(related);
    } catch (err) {
      console.error('Error fetching blog post:', err);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateReadTime = (content) => {
    if (!content) return '3 min';
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShare = (platform) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(post?.title || '');
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="blog-post-page">
          <div className="blog-post-loading">
            <div className="loading-spinner" />
            <p>Loading article...</p>
          </div>
        </div>
      </>
    );
  }

  if (!post) return null;

  return (
    <>
      <Header />
      <div className="blog-post-page">
        <article className="blog-post">
          <Link to="/blog" className="blog-post-back">
            <ArrowLeft size={18} />
            Back to Resources
          </Link>

          <header className="blog-post-header">
            {post.category && (
              <span className="blog-post-category">{post.category}</span>
            )}
            <h1 className="blog-post-title">{post.title}</h1>
            
            <div className="blog-post-meta">
              <span className="blog-post-date">
                <Calendar size={16} />
                {formatDate(post.published_at)}
              </span>
              {post.author && (
                <span className="blog-post-author">
                  <User size={16} />
                  {post.author}
                </span>
              )}
              <span className="blog-post-readtime">
                <Clock size={16} />
                {calculateReadTime(post.content)}
              </span>
            </div>
          </header>

          {post.featured_image && (
            <div className="blog-post-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}

          <div 
            className="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Share buttons */}
          <div className="blog-post-share">
            <span className="share-label">
              <Share2 size={16} />
              Share this article
            </span>
            <div className="share-buttons">
              <button onClick={() => handleShare('twitter')} aria-label="Share on Twitter">
                <Twitter size={18} />
              </button>
              <button onClick={() => handleShare('linkedin')} aria-label="Share on LinkedIn">
                <Linkedin size={18} />
              </button>
              <button onClick={() => handleShare('facebook')} aria-label="Share on Facebook">
                <Facebook size={18} />
              </button>
            </div>
          </div>

          {/* CTA */}
          <div className="blog-post-cta">
            <h3>Ready to secure your digital life?</h3>
            <p>Learn practical steps to protect yourself online with our comprehensive course.</p>
            <Link to="/courses/cyber-privacy-security-basics" className="cta-btn">
              View Course
            </Link>
          </div>
        </article>

        {/* Related posts */}
        {relatedPosts.length > 0 && (
          <aside className="blog-related">
            <h3>Related Articles</h3>
            <div className="related-grid">
              {relatedPosts.map(relatedPost => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug}`}
                  className="related-card"
                >
                  {relatedPost.featured_image && (
                    <img src={relatedPost.featured_image} alt={relatedPost.title} />
                  )}
                  <h4>{relatedPost.title}</h4>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </div>
    </>
  );
};

export default BlogPost;
